<template>
    <div id="atlas-block"
        class="atlas-container"
    >
        <Logo
            :show_custom="true"
            :custom_logo="custom_logo"
            color="Blue"
            :cwa_logo_src="`/static/UIUX/images/header/${logo_file_name}_Blue_web@3x.png`"
        />
        <div class="atlas">
            <div id="atlas-wra" 
                class="atlas-table-page"
            >
                <div class="atlas-table-page__container">
                    <div v-for="(data, template_index) in current_template"
                        :key="`template_${data.tag}_${template_index}`"
                    >
                        <template v-if="data.tag === 'title'">
                            <div class="atlas__title">
                                {{ data.text }}
                            </div>
                        </template>
                        <template v-if="data.tag === 'subtitle'">
                            <AtlasIntroduction
                                :titles="data.titles"
                                :en_title="data.title_en"
                                :update_messages="data.update_messages"
                            />
                        </template>
                        <template v-if="data.tag === 'datetime-select'">
                            <AtlasDatetimeSelect
                                :default_value="image_type"
                                :timestamp="current_timestamp"
                                @update_timestamp="update_timestamp"
                            ></AtlasDatetimeSelect>
                        </template>
                        <template v-if="data.tag === 'date-table'">
                            <AtlasTableDate
                                class="table-date"
                                :class="data.data_class"
                                :key="`${current_date.name}_${data.tag}_${template_index}`"
                                :id="`table-date-${template_index}`"
                                :row_date_count="data.row_date_count"
                                :column_date_count="data.column_date_count"
                                :images_dir="data.images_dir"
                                :file_name="data.file_name"
                                :compression="data.compression"
                                @open-image-dialog="open_image_dialog"
                            />
                        </template>
                        <template v-if="data.tag === 'date-single-table'">
                            <AtlasSingleTableDate
                                class="table-date"
                                :class="data.data_class"
                                :key="`${current_date.name}_${data.tag}_${template_index}`"
                                :id="`table-date-${template_index}`"
                                :row_date_count="data.row_date_count"
                                :column_date_count="data.column_date_count"
                                :images_dir="data.images_dir"
                                :file_name="data.file_name"
                                :compression="data.compression"
                                @open-image-dialog="open_image_dialog"
                            />
                        </template>
                        <template v-if="data.tag === 'grid'">
                            <div
                                v-if="data.images.length"
                                class="atlas-grid__divide"
                                :key="`current_ts_${current_timestamp}`"
                                :style="`grid-template-columns: repeat(${data.grid}, 1fr);`"
                            >
                                <AtlasGrid v-for="(image, image_index) in data.images"
                                    :key="`${current_date.name}_${data.tag}_${template_index}_${image_index}`"
                                    :image="image"
                                    :class="{'center': image.center}"
                                    @open-image-dialog="open_image_dialog"
                                />
                            </div>
                        </template>
                        <template v-if="data.tag === 'grid-bind'">
                            <div
                                v-if="data.images.length"
                                class="atlas-grid__divide"
                                :style="`grid-template-columns: repeat(${data.images.length}, 1fr);`"
                            >
                                <div v-for="(data_images, data_images_index) in data.images"
                                    :key="`bind_${data_images_index}`"
                                >
                                    <AtlasGrid v-for="(image, image_index) in data_images"
                                        :key="image.src"
                                        :image="image"
                                        @open-image-dialog="open_image_dialog"
                                    />
                                </div>
                            </div>
                        </template>
                        <template v-if="data.tag === 'annotation'">
                            <div :class="{'atlas-grid__left-right': data.left_right}">
                                <div class="atlas__annotation">
                                    <template v-if="data.hide">
                                        <h3 class="atlas__annotation__btn"
                                            @click="(is_show_annotation[template_index] = !is_show_annotation[template_index])"
                                        >
                                            {{ data.text }}
                                        </h3>
                                        <template v-if="data.list">
                                            <ol v-show="is_show_annotation[template_index]"
                                                class="atlas__annotation__content" 
                                                :class="{ 'atlas__annotation__content--visible': is_show_annotation[template_index]}"
                                            >
                                                <li v-for="(text, text_index) in data.list"
                                                    :key="`list_text_${text_index}`"
                                                >
                                                    {{ text }}
                                                </li>
                                            </ol>
                                        </template>
                                        <template v-if="data.image">
                                            <img v-show="is_show_annotation[template_index]"
                                                :alt="data.image.alt"
                                                class="atlas__annotation" 
                                                :src="data.image.src"
                                                :width="`${data.width || '100%'}`"
                                                height="100%"
                                            >
                                        </template>
                                    </template>
                                    <template v-else>
                                        {{ data.text }}
                                        <template v-if="data.image">
                                            <img
                                                :alt="data.image.alt"
                                                :src="data.image.src"
                                                :width="`${data.width || '100%'}`"
                                                height="100%"
                                            >
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </template>
                        <template v-if="data.tag === 'note'">
                            <div class="atlas__note">
                                {{ data.text }}
                            </div>
                        </template>
                        <template v-if="data.tag === 'link'">
                            <div class="atlas-link">
                                <a class="atlas-link__link"
                                    :href="data.href"
                                    target="_blank"
                                >
                                    {{ data.text }}
                                    <v-icon>mdi-launch</v-icon>
                                </a>
                            </div>
                        </template>
                        <template v-if="data.tag === 'title-left-right'">
                            <div class="atlas-grid__left-right">
                                <div class="atlas__title">
                                    {{ data.text_left }}
                                </div>
                                <div class="atlas__title">
                                    {{ data.text_right }}
                                </div>
                            </div>
                        </template>
                        <template v-if="data.tag === 'subtitle-left-right'">
                            <div class="atlas-grid__left-right">
                                <AtlasIntroduction
                                    :titles="data.titles_left"
                                    :en_title="data.title_en_left"
                                    :update_messages="data.update_messages_left"
                                />
                                <AtlasIntroduction
                                    :titles="data.titles_right"
                                    :en_title="data.title_en_right"
                                    :update_messages="data.update_messages_right"
                                />
                            </div>
                        </template>
                        <template v-if="data.tag === 'header-fill'">
                            <div class="atlas-grid__left-right">
                                <div class="atlas-grid__table-header-fill">
                                    {{ data.text }}
                                </div>
                            </div>
                        </template>
                        <template v-if="data.tag === 'grid-left-right'">
                            <div class="atlas-grid__left-right">
                                <div>
                                    <AtlasGrid v-for="(image, image_index) in data.images_left"
                                        :key="`${current_date.name}_${data.tag}_${template_index}_left${image_index}`"
                                        :image="image"
                                        @open-image-dialog="open_image_dialog"
                                    />
                                </div>
                                <div>
                                    <AtlasGrid v-for="(image, image_index) in data.images_right"
                                        :key="`${current_date.name}_${data.tag}_${template_index}_right${image_index}`"
                                        :image="image"
                                        @open-image-dialog="open_image_dialog"
                                    />
                                </div>
                            </div>
                        </template>
                        <template v-if="data.tag === 'multiple-chart'">
                            <MultipleChart
                                :zone_span="data.zone_span"
                                :option_local_storage_name="data.option_local_storage_name"
                            />
                        </template>
                        <template v-if="data.tag === 'time-select-table'">
                            <AtlasTimeSelectTable
                                :class="data.data_class"
                                :key="`${current_date.name}_${data.tag}_${template_index}`"
                                :current_data="data"
                                @open-image-dialog="open_image_dialog"
                            />
                        </template>
                        <template v-if="data.tag === 'help-icon'">
                            <div>
                                <v-btn
                                    class="btn-icon"
                                    @click="show_help_dialog = true"
                                    fab
                                    x-small
                                >
                                    <v-icon>icon-help</v-icon>
                                    <span class="btn-text"> 說明 </span>
                                </v-btn>

                                <v-dialog
                                    v-model="show_help_dialog"
                                    width="fit-content"
                                >
                                    <v-card>
                                        <v-card-title style="background-color: #2366f8;">
                                            <div style="color: white;"> 
                                                說明 
                                            </div>
                                            <v-spacer />
                                            <v-btn
                                                icon
                                                @click="show_help_dialog = false"
                                            >
                                                <v-icon style="color: white;"> 
                                                    mdi-close 
                                                </v-icon>
                                            </v-btn>
                                        </v-card-title>
                                        <img
                                            style="margin-top: 10px;"
                                            :alt="data.image.alt"
                                            :src="data.image.src"
                                            width="100%"
                                        >
                                    </v-card>
                                </v-dialog>
                            </div>
                        </template>
                        <template v-if="data.tag === 'grid-padding'">
                            <div
                                v-if="data.images.length"
                                class="atlas-grid__divide"
                                :style="`grid-template-columns: repeat(${data.grid}, 1fr); padding: 15px 10px 10px;`"
                            >
                                <AtlasGrid v-for="(image, image_index) in data.images"
                                    :key="`${current_date.name}_${data.tag}_${template_index}_${image_index}`"
                                    :image="image"
                                    :class="{'center': image.center}"
                                    @open-image-dialog="open_image_dialog"
                                />
                            </div>
                        </template>
                        <template v-if="data.tag === 'btn-area'">
                            <div class="btn-area">  
                                <v-btn
                                    class="btn-icon"
                                    @click="show_help_dialog_2 = true"
                                    fab
                                    x-small
                                >
                                    <v-icon>icon-help</v-icon>
                                    <span class="btn-text"> 說明 </span>
                                </v-btn>

                                <v-btn 
                                    class="btn-icon"
                                    @click="download_csv"
                                    fab
                                    x-small
                                >
                                    <v-icon color="#2366f8">
                                        icon-file-download
                                    </v-icon>
                                    <span class="btn-text">下載表格CSV</span>
                                </v-btn>

                                <v-dialog
                                    v-model="show_help_dialog_2"
                                    width="fit-content"
                                >
                                    <v-card>
                                        <v-card-title style="background-color: #2366f8;">
                                            <div style="color: white;"> 
                                                說明 
                                            </div>
                                            <v-spacer />
                                            <v-btn
                                                icon
                                                @click="show_help_dialog_2 = false"
                                            >
                                                <v-icon style="color: white;"> 
                                                    mdi-close 
                                                </v-icon>
                                            </v-btn>
                                        </v-card-title>
                                        <img
                                            style="margin-top: 10px;"
                                            :alt="data.image.alt"
                                            :src="data.image.src"
                                            width="100%"
                                        >
                                    </v-card>
                                </v-dialog>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <v-dialog
            v-model="dialog"
            height="100%"
            content-class="atlas-dialog"
            fullscreen
        >
            <div 
                class="atlas-dialog__container"
                @click="close_atlas_dialog"
            >
                <img
                    :src="dialog_image_path"
                    @click="maintain_atlas_dialog"
                >
                <v-btn 
                    class="atlas-dialog__close-btn" 
                    icon 
                    dark 
                    x-large
                    @click="close_atlas_dialog" 
                >
                    <v-icon>icon-close</v-icon>
                </v-btn>
            </div>
        </v-dialog>
    </div>
</template>

<script>
import * as dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)
import { atlas_root_dir, alt_src, white_src, data_list } from '../../config/atlas_route_config.js'
import { atlas_newest_data_ajax } from '@/utils/atlas/atlas.js'
import { mapGetters, mapState } from 'vuex'

import Logo from '../logo.vue'

export default {
    name: 'AtlasBlock',

    components: {
        AtlasDatetimeSelect: () =>
            import(/* webpackPrefetch: true */ '@/components/atlas/_tool/atlas_datetime_select.vue'),
        AtlasTableDate: () => import(/* webpackPrefetch: true */ '@/components/atlas/_tool/atlas_table_date.vue'),
        AtlasSingleTableDate: () =>
            import(/* webpackPrefetch: true */ '@/components/atlas/_tool/atlas_table_single_date.vue'),
        AtlasIntroduction: () => import(/* webpackPrefetch: true */ '@/components/atlas/_tool/atlas_introduction.vue'),
        AtlasGrid: () => import(/* webpackPrefetch: true */ '@/components/atlas/_tool/atlas_grid.vue'),
        MultipleChart: () => import('@/components/atlas/_tool/multiple_chart.vue'),
        AtlasTimeSelectTable: () => import('@/components/atlas/atlas_time_select_table.vue'),
        Logo,
    },

    data() {
        return {
            alt_src: alt_src,
            white_src: white_src,
            data_list: data_list,
            current_timestamp: '',
            current_template: [],
            is_show_annotation: {},
            current_date: {},

            dialog: false,
            dialog_image_path: '',
            show_help_dialog: false,
            show_help_dialog_2: false,
        }
    },

    computed: {
        ...mapState(['client_name']),

        ...mapGetters(['logo_file_name', 'is_mobile_width']),

        ...mapGetters('atlas', {
            image_type: 'type',
        }),

        custom_logo() {
            return `/static/${this.client_name}/images/header/${this.is_mobile_width ? 'logo_m' : 'logo'}.png`
        },

        default_datetime_select_add_mins(){
            // datetime_select預設時間 = 資料時間 + add_mins
            const item =this.current_date.data_template.find(o => o.tag === 'datetime-select')
            if(!item || !item.add_mins) return 0
            
            return item.add_mins
        }
    },

    watch: {
        image_type: {
            immediate: true,
            handler() {
                this.current_timestamp = ''
                this.current_template = []
                this.current_date = this.data_list.find(({ image_type }) => this.image_type === image_type) || {}
            },
        },

        'current_date.image_type': {
            immediate: true,
            deep: true,
            handler() {
                this.init_src()
            },
        },

        current_timestamp(ts) {
            //有時間選單的產品, 更新時間後要重新組圖
            this.init_src()
        },
    },

    methods: {
        async init_src() {
            const { grid_dir, data_template } = this.current_date
            if (!data_template.length) console.error('沒有 template')
            data_template.forEach((item, index) => {
                const { tag, hide } = item
                if (tag === 'annotation' && hide) {
                    this.$set(this.is_show_annotation, index, false)
                }
            })

            const has_grid = data_template.some(({ tag }) => tag.includes('grid'))

            if (has_grid && !grid_dir) console.error('data_template 包含有 tag 為 grid 的區塊，卻沒有提供 grid_dir')
            if (grid_dir) {
                const path_list = grid_dir.map((image_dir) => {
                    return `${atlas_root_dir}${image_dir}`
                })
                const new_template = await this.get_all_img_data(path_list)
                this.current_template = new_template
            }

            this.current_template = data_template || []
        },

        async get_all_img_data(path_list) {
            const { grid_dir, use_fulldate, data_template } = this.current_date

            const responses = await atlas_newest_data_ajax(path_list)
            const date_list = responses.map((res) => (use_fulldate ? [res.data] : this._split_date_hour(res.data)))
            return this.get_file_src(data_template, grid_dir, date_list)
        },

        get_file_src(data_template, dir, date_list) {
            const rule_func = {
                pre_date_suf: ({ prefix, suffix, type, dir, dir_index, newsest_date }) => {
                    return `${atlas_root_dir}${dir[dir_index]}${prefix}${newsest_date}${suffix}.${type}`
                },
                pre_date_hour_suf: ({ prefix, suffix, type, dir, newsest_date, newest_hour }) => {
                    return `${atlas_root_dir}${dir}${prefix}${newsest_date}${newest_hour}${suffix}.${type}`
                },
                date_pre_hour_suf: ({ prefix, suffix, type, dir, dir_index, newsest_date, newest_hour }) => {
                    return `${atlas_root_dir}${dir[dir_index]}${newsest_date}${prefix}${newest_hour}${suffix}.${type}`
                },
                pre_year_month_suf: ({ prefix, suffix, type, dir, year, month }) => {
                    return `${atlas_root_dir}${dir}${prefix}${year}${month}${suffix}.${type}`
                },
                pre_year_middle_month_suf: ({ prefix, middle, suffix, type, dir, year, month }) => {
                    return `${atlas_root_dir}${dir}${prefix}${year}${middle}${month}${suffix}.${type}`
                },
                // date_pre_year_suf: ({ prefix, suffix, type, dir, newsest_date, year, month, newest_hour }) => {
                //     return `${atlas_root_dir}${dir}${prefix}${newsest_date}${suffix}.${type}`;
                // },
                wra_droughtindex: ({ dir_index, suffix, type, year, month }) => {
                    return `${atlas_root_dir}${dir[dir_index]}${suffix}${year}_${month}.${type}`
                },
                dayjs_format: ({ dir_index, prefix, suffix, timestamp, format, utc_offset, add_mins, type }) => {
                    let ds = dayjs(timestamp)
                    if (add_mins) ds = ds.add(add_mins, 'minute')

                    const datetime_txt = ds.utcOffset(utc_offset).format(format)
                    return `${atlas_root_dir}${dir[dir_index]}${prefix}${datetime_txt}${suffix}.${type}`
                },
            }

            const THIS = this
            const format_src = function(item) {
                const { prefix, middle, suffix, type, dir_index, rule, format, utc, add_mins } = item
                const [newsest_date, newest_hour] = date_list[dir_index]
                const year = `${newsest_date}`.substring(0, 4)
                const month = `${newsest_date}`.substring(4, 6)
                const utc_offset = utc ?? dayjs().utcOffset() / 60
                const timestamp = dayjs
                    .utc(`${newsest_date}${newest_hour}`, 'YYYYMMDDHHmm')
                    .subtract(utc_offset, 'hour')
                    .valueOf()

                let current_timestamp = timestamp
                if (THIS.current_timestamp) {
                    current_timestamp = THIS.current_timestamp //如果有datetime-select, 要用這個值替代timestamp
                    item.failed = false //恢復預設
                } else {
                    //如果還沒有current_timestamp的話, 用這筆資料來設定預設的時間
                    THIS.current_timestamp = timestamp + THIS.default_datetime_select_add_mins * 1000 * 60 //紀錄timestamp
                }

                item.timestamp = current_timestamp
                item.src = rule_func[rule]({
                    prefix,
                    middle,
                    suffix,
                    type,
                    dir,
                    dir_index,
                    newsest_date,
                    year,
                    month,
                    newest_hour,
                    format,
                    utc_offset,
                    add_mins,
                    timestamp: current_timestamp,
                })
            }

            return data_template.map((tag_item) => {
                const { tag } = tag_item
                if (tag === 'grid') {
                    tag_item.images.map((data) => {
                        format_src(data)
                        return data
                    })
                } else if (tag === 'grid-bind') {
                    tag_item.images.map((data) => {
                        data.map((item) => format_src(item))
                        return data
                    })
                } else if (tag === 'grid-left-right') {
                    tag_item.images_left.map((data) => {
                        format_src(data)
                        return data
                    })
                    tag_item.images_right.map((data) => {
                        format_src(data)
                        return data
                    })
                } else if (tag === 'grid-padding') {
                    tag_item.images.map((data) => {
                        format_src(data)
                        return data
                    })
                }
                return tag_item
            })
        },

        update_timestamp(ts) {
            this.current_timestamp = ts
        },

        _split_date_hour(datetime_string) {
            const [split_date, newest_hour] = datetime_string.split(',')
            const newsest_date = split_date.replace(/-/g, '')
            return [newsest_date, newest_hour]
        },

        open_image_dialog(image) {
            this.dialog = true
            this.dialog_image_path = image
        },

        close_atlas_dialog() {
            this.dialog = false
            this.dialog_image_path = ''
        },

        maintain_atlas_dialog(event) {
            // 用來阻止點擊 img container 的 close_atlas_dialog 事件(內層外層的問題)
            event.stopPropagation()
        },

        download_csv() {
            const link = document.createElement('a')
            link.setAttribute('id', 'download_csv')
            link.setAttribute(
                'href',
                `${window.location.origin}/static/data/images/wra/monthly/ECMWF/SEAS5_6cluster_sheet_qmap_EP.csv`,
            )
            link.setAttribute('download', 'SEAS5_6cluster_sheet_qmap_EP')

            document.body.appendChild(link)
            link.click()
            document.getElementById('download_csv').remove()
        },
    },
}
</script>

<style lang="scss">
@import '../../../css/mixin';

.init_dates {
    top: 50%;
    text-align: center;
}

.init_date__container {
    display: table-cell;
    vertical-align: middle;
    width: inherit;
    height: inherit;
}

.atlas-container {
    height: 100vh;
    overflow: auto;
}

.atlas {
    padding: 1rem;
    background: #fff;

    &__title {
        margin-bottom: 0.5rem;
        padding: 0.5rem 0;
        width: 100%;
        font-size: 1.5rem;
        letter-spacing: 1px;
        line-height: 2rem;
        text-align: center;
        color: #fff;
        background: var(--base-blue);
    }
}

.atlas-dialog {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100% !important;
    max-height: 100%;
    background-color: rgba(0, 0, 0, 0.9);

    &__container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow-y: auto;
        margin: 1rem;
        height: 90vh;
    }

    &__close-btn {
        position: absolute;
        top: 0;
        right: 0;
        align-self: flex-start;
    }

    img {
        display: block;
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: 100%;
    }
}
</style>

<style lang="scss" scoped>
@import '../../../css/mixin';

@mixin grid-column($start, $end) {
    grid-column-start: $start;
    grid-column-end: $end;
}

@mixin grid-2fr-fillup {
    @include grid-column(1, 3);
}

.atlas-grid {
    &__left-right {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-column-gap: 0.5rem;

        @include laptop {
            min-width: 1024px;
        }

        .atlas__annotation {
            @include grid-2fr-fillup;

            text-align: center;
        }
    }

    &__table-header-fill {
        @include grid-2fr-fillup;
        margin: 0.5rem 0 0;
        padding: 6px;
        background: var(--white-bg);
        text-align: center;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 1px;
        font-weight: bold;
    }
}

.atlas-grid__divide {
    display: grid;
    column-gap: 0.5rem;

    .center {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    @include laptop {
        grid-template-columns: auto !important;
        column-gap: 0;
    }
}

.atlas-link {
    text-align: center;

    &__link,
    i {
        font-weight: bold;
        margin: auto;
        color: var(--base-dark-blue) !important;
        transition: opacity 0.25 linear;

        &:hover {
            opacity: 0.6;
            transition: opacity 0.25 linear;
        }
    }
}

.btn-icon {
    margin: 5px 0 0 20px;

    .btn-text {
        position: absolute;
        transform: translateY(-25px);
        display: none;
        background: #fff;
    }

    &:hover .btn-text {
        text-transform: capitalize;
        display: block;
    }
}
</style>
